import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Divider,
  IconButton,
  Box,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import InventoryIcon from "@mui/icons-material/Inventory";
import ShoppingCartCheckoutOutlinedIcon from "@mui/icons-material/ShoppingCartCheckoutOutlined";
import DeliveryDiningOutlinedIcon from "@mui/icons-material/DeliveryDiningOutlined";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import { formFieldStyles } from "../../styles/formStyles";
import { IDepartmentMinDTO, ICreateRoleDTO, IPermissionMinDTO, IRoleListDTO, IRoleDeptsPermDTO } from "../../interfaces/UserManagement";
import { getDeptsMin, getPermissionsMin, createRole, updateRole, getRoleDeptsperms } from "../../services/UserManagement";
import { useAuth } from "../../auth/AuthProvider";

const MODULE_CONFIG: Record<string, { icon: any; iconBg: string; iconColor: string; description: string }> = {
  "Inventory Management": { icon: InventoryIcon, iconBg: "rgba(0, 94, 255, 0.10)", iconColor: "#005EFF", description: "Control stock levels, SKUs, and adjustments" },
  "Order processing": { icon: ShoppingCartCheckoutOutlinedIcon, iconBg: "rgba(52, 199, 89, 0.10)", iconColor: "#00B221", description: "Manage customer orders and returns" },
  "Delivery Operations": { icon: DeliveryDiningOutlinedIcon, iconBg: "rgba(138, 43, 226, 0.10)", iconColor: "#8A2BE2", description: "Track vehicles, drivers and maintenance" },
  "User Management": { icon: PeopleOutlinedIcon, iconBg: "rgba(0, 94, 255, 0.10)", iconColor: "#005EFF", description: "Manage users, roles and departments" },
  "Settings": { icon: SettingsOutlinedIcon, iconBg: "rgba(74, 85, 101, 0.10)", iconColor: "#61708A", description: "Warehouse and inventory settings" },
  "Organisation Settings": { icon: CorporateFareOutlinedIcon, iconBg: "rgba(74, 85, 101, 0.10)", iconColor: "#61708A", description: "Organization settings" },
  "Profile Settings": { icon: AccountCircleOutlinedIcon, iconBg: "rgba(74, 85, 101, 0.10)", iconColor: "#61708A", description: "Profile settings" },
};

interface PermissionGroup {
  module: string;
  sections: { page: string; permissions: { id: number; name: string }[] }[];
}

const groupPermissions = (permissions: IPermissionMinDTO[]): PermissionGroup[] => {
  const MODULE_ORDER = ["Inventory Management", "Order processing", "Delivery Operations", "User Management", "Settings", "Organisation Settings", "Profile Settings"];
  const PAGE_KEYWORD_MAP: Record<string, string> = {
    "Add Inventory Items": "Inventory Management|Items",
    "Edit Inventory items": "Inventory Management|Items",
    "View Inventory Items": "Inventory Management|Items",
    "View transfer Item": "Inventory Management|Transfers",
    "Move Items": "Inventory Management|Transfers",
    "Receive Item in Building": "Inventory Management|Transfers",
    "Receive Item in Room": "Inventory Management|Transfers",
    "Transfer Item to Bin": "Inventory Management|Transfers",
    "Add Order": "Order processing|Orders",
    "Edit Order": "Order processing|Orders",
    "View Order": "Order processing|Orders",
    "Pick Order items": "Order processing|Orders",
    "Pack Order items": "Order processing|Orders",
    "Assign Packages": "Delivery Operations|Delivery Overview",
    "Re-assign Packages": "Delivery Operations|Delivery Overview",
    "View Packages": "Delivery Operations|Delivery Overview",
    "View Assignments": "Delivery Operations|Delivery Assignments",
    "Add Departments": "User Management|Departments",
    "Edit Departments": "User Management|Departments",
    "View Departments": "User Management|Departments",
    "Add Roles": "User Management|Roles",
    "Edit Roles": "User Management|Roles",
    "View Roles": "User Management|Roles",
    "Add users": "User Management|Users",
    "Edit Users": "User Management|Users",
    "View Users": "User Management|Users",
    "Invite Users": "User Management|Users",
    "Add Building": "Settings|Warehouse Settings",
    "Edit Building": "Settings|Warehouse Settings",
    "View Building": "Settings|Warehouse Settings",
    "Add Floor": "Settings|Warehouse Settings",
    "Edit Floor": "Settings|Warehouse Settings",
    "View Floor": "Settings|Warehouse Settings",
    "Add Room": "Settings|Warehouse Settings",
    "Edit Room": "Settings|Warehouse Settings",
    "View Room": "Settings|Warehouse Settings",
    "Delete Room": "Settings|Warehouse Settings",
    "Add Rack": "Settings|Warehouse Settings",
    "Edit Rack": "Settings|Warehouse Settings",
    "View Rack": "Settings|Warehouse Settings",
    "Delete Rack": "Settings|Warehouse Settings",
    "Add Bin": "Settings|Warehouse Settings",
    "Edit Bin": "Settings|Warehouse Settings",
    "View Bin": "Settings|Warehouse Settings",
    "Delete Bin": "Settings|Warehouse Settings",
    "Add Type": "Settings|Inventory Settings",
    "Edit Type": "Settings|Inventory Settings",
    "View Type": "Settings|Inventory Settings",
    "Delete Type": "Settings|Inventory Settings",
    "Add Category": "Settings|Inventory Settings",
    "Edit Category": "Settings|Inventory Settings",
    "View Category": "Settings|Inventory Settings",
    "Delete Category": "Settings|Inventory Settings",
    "Add Item": "Settings|Inventory Settings",
    "Edit Item": "Settings|Inventory Settings",
    "View Item": "Settings|Inventory Settings",
    "Delete Item": "Settings|Inventory Settings",
    "Organisation Tab": "Organisation Settings|Organisation Settings",
    "Edit Organisation": "Organisation Settings|Organisation Settings",
    "Edit Profile": "Profile Settings|Profile Settings",
  };

  const moduleMap = new Map<string, Map<string, { id: number; name: string }[]>>();

  for (const p of permissions) {
    const mapping = PAGE_KEYWORD_MAP[p.permissionName];
    if (!mapping) continue;
    const [module, page] = mapping.split("|");
    if (!moduleMap.has(module)) moduleMap.set(module, new Map());
    const pageMap = moduleMap.get(module)!;
    if (!pageMap.has(page)) pageMap.set(page, []);
    pageMap.get(page)!.push({ id: p.id, name: p.permissionName });
  }

  return MODULE_ORDER
    .filter((m) => moduleMap.has(m))
    .map((module) => {
      const pageMap = moduleMap.get(module)!;
      return {
        module,
        sections: Array.from(pageMap.entries()).map(([page, perms]) => ({ page, permissions: perms })),
      };
    });
};

interface RoleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editingRole: IRoleListDTO| null;
}

const RoleDialog = ({ open, onClose, onSave, editingRole }: RoleDialogProps) => {
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const [roleName, setRoleName] = useState("");
  const [roleNameError, setRoleNameError] = useState("");
  const [departments, setDepartments] = useState<IDepartmentMinDTO[]>([]);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [selectedDeptIds, setSelectedDeptIds] = useState<number[]>([]);
  const [selectedPermIds, setSelectedPermIds] = useState<number[]>([]);
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [openError, setOpenError] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const deptRes = await getDeptsMin();
      setDepartments(deptRes && Array.isArray(deptRes) ? deptRes : []);
      const permRes = await getPermissionsMin();
      const perms: IPermissionMinDTO[] = permRes && permRes && Array.isArray(permRes) ? permRes : [];
      setPermissionGroups(groupPermissions(perms));
    })();
  }, [open]);

  useEffect(() => {
    if (open && editingRole) {
      setRoleName(editingRole.roleName || "");
      setRoleNameError("");
      (async () => {
        const res: IRoleDeptsPermDTO = await getRoleDeptsperms(editingRole.roleId);
        if (res) {
          setSelectedDeptIds(res.departments?.map((d) => d.id) || []);
          setSelectedPermIds(res.permissions?.map((p) => p.id) || []);
        }
      })();
    } else if (open) {
      setRoleName("");
      setSelectedDeptIds([]);
      setSelectedPermIds([]);
      setRoleNameError("");
    }
  }, [open, editingRole]);

  const toggleDept = (id: number) => {
    setSelectedDeptIds((prev) => prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]);
  };

  const togglePerm = (id: number) => {
    setSelectedPermIds((prev) => prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]);
  };

  const toggleSelectAllSection = (perms: { id: number; name: string }[]) => {
    const ids = perms.map((p) => p.id);
    const allSelected = ids.every((id) => selectedPermIds.includes(id));
    setSelectedPermIds((prev) => allSelected ? prev.filter((id) => !ids.includes(id)) : Array.from(new Set([...prev, ...ids])));
  };

  const handleValidate = (): boolean => {
    if (!roleName.trim()) {
      setRoleNameError("Role name is required");
      return false;
    }
    setRoleNameError("");
    return true;
  };

  const handleSave = async () => {
    if (editingRole) {
      await handleUpdate();
    } else {
      await handleCreate();
    }
  };

  const handleCreate = async () => {
    if (!handleValidate()) return;
    const warehouseId = loggedInUser?.workspaceId;
    if (!warehouseId) {
      setOpenError(true);
      setMsg("Warehouse information is missing.");
      return;
    }
    setLoading(true);
    try {
      const post: ICreateRoleDTO = {
        roleName,
        warehouseId,
        departmentIds: selectedDeptIds,
        permissionIds: selectedPermIds,
      };
      const response = await createRole(post);
      if (response && response.status === "success") {
        setOpenSuccess(true);
        setMsg("New role created successfully.");
        onSave();
        onClose();
      } else {
        setOpenError(true);
        setMsg("Error creating role.");
      }
    } catch {
      setOpenError(true);
      setMsg("Error creating role.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!handleValidate()) return;
    const warehouseId = loggedInUser?.workspaceId;
    if (!warehouseId || !editingRole?.roleId) {
      setOpenError(true);
      setMsg("Warehouse or role information is missing.");
      return;
    }
    setLoading(true);
    try {
      const post: ICreateRoleDTO = {
        roleName,
        warehouseId,
        departmentIds: selectedDeptIds,
        permissionIds: selectedPermIds,
      };
      const response = await updateRole(post, editingRole.roleId);
      if (response && response.status === "success") {
        setOpenSuccess(true);
        setMsg("Role updated successfully.");
        onSave();
        onClose();
      } else {
        setOpenError(true);
        setMsg("Error updating role.");
      }
    } catch {
      setOpenError(true);
      setMsg("Error updating role.");
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setOpenError(false);
    setOpenSuccess(false);
    setMsg("");
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1, pt: 2.5, px: 3 }}>
          <Box>
            <Typography sx={{ color: "#000814", fontSize: "16px", fontWeight: 700, lineHeight: "24px", letterSpacing: "-0.312px" }}>{editingRole ? "Edit Role" : "Add Role"}</Typography>
            <Typography sx={{ color: "#61708A", fontSize: "14px", fontWeight: 400, mt: 0.5 }}>{editingRole ? "Edit Role" : "Add Role"}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ position: "absolute", right: 16, top: 16 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3, maxHeight: "60vh", overflowY: "auto", "&::-webkit-scrollbar": { display: "none" }, msOverflowStyle: "none", scrollbarWidth: "none" }}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ mb: 1, fontSize: "14px", fontWeight: 500, color: "text.primary" }}>Role Name <Typography component="span" sx={{ color: "error.main" }}>*</Typography></Typography>
            <TextField
              fullWidth
              placeholder="Inventory manager"
              value={roleName}
              onChange={(e) => { setRoleName(e.target.value); setRoleNameError(""); }}
              error={!!roleNameError}
              helperText={roleNameError}
              sx={formFieldStyles}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography sx={{ mb: 1.5, color: "#000814", fontSize: "16px", fontWeight: 700, lineHeight: "24px", letterSpacing: "-0.312px" }}>Departments</Typography>
            <Box sx={{ p: 2, borderRadius: "8px", border: "1px solid rgba(74, 85, 101, 0.10)", bgcolor: "#F9FAFB", display: "flex", flexWrap: "wrap", gap: 1 }}>
              {departments.map((dept) => (
                <Box
                  key={dept.id}
                  onClick={() => toggleDept(dept.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: selectedDeptIds.includes(dept.id) ? "primary.main" : "rgba(74, 85, 101, 0.10)",
                    bgcolor: selectedDeptIds.includes(dept.id) ? "primary.light" : "#F9FAFB",
                    cursor: "pointer",
                  }}
                >
                  <Checkbox checked={selectedDeptIds.includes(dept.id)} size="small" sx={{ p: 0 }} />
                  <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#000814", lineHeight: "21px" }}>{dept.departmentName}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <LockPersonIcon sx={{ color: "primary.main", fontSize: "20px" }} />
            <Typography sx={{ color: "#000814", fontSize: "16px", fontWeight: 700, lineHeight: "24px", letterSpacing: "-0.312px" }}>Role Permissions</Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {permissionGroups.map((group) => {
              const config = MODULE_CONFIG[group.module] || MODULE_CONFIG["Settings"];
              const Icon = config.icon;
              const isExpanded = expandedModule === group.module;
              return (
                <Box key={group.module}>
                  <Box
                    onClick={() => setExpandedModule(isExpanded ? null : group.module)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      bgcolor: "background.default",
                      border: "1px solid rgba(74, 85, 101, 0.10)",
                      borderRadius: isExpanded ? "8px 8px 0 0" : "8px",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#F4F4F4" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "40px", height: "40px", borderRadius: "8px", bgcolor: config.iconBg, border: "1px solid rgba(74, 85, 101, 0.10)" }}>
                      <Icon sx={{ fontSize: "20px", color: config.iconColor }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#000814", lineHeight: "21px" }}>{group.module}</Typography>
                      <Typography sx={{ fontSize: "12px", color: "#61708A", lineHeight: "18px" }}>{config.description}</Typography>
                    </Box>
                  </Box>

                  {isExpanded && (
                    <Box sx={{ p: 2, bgcolor: "#FFF", border: "1px solid rgba(74, 85, 101, 0.10)", borderTop: "none", borderRadius: "0 0 8px 8px" }}>
                      {group.sections.map((section, idx) => (
                        <Box key={section.page} sx={{ mb: idx < group.sections.length - 1 ? 2 : 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                            <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "grey.700", textTransform: "uppercase" }}>{section.page}</Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography
                                onClick={() => toggleSelectAllSection(section.permissions)}
                                sx={{ fontSize: "12px", color: "primary.main", cursor: "pointer", fontWeight: 500 }}
                              >
                                Select All
                              </Typography>
                              <Checkbox
                                checked={section.permissions.every((p) => selectedPermIds.includes(p.id))}
                                indeterminate={section.permissions.some((p) => selectedPermIds.includes(p.id)) && !section.permissions.every((p) => selectedPermIds.includes(p.id))}
                                onChange={() => toggleSelectAllSection(section.permissions)}
                                size="small"
                              />
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {section.permissions.map((perm) => (
                              <Box
                                key={perm.id}
                                onClick={() => togglePerm(perm.id)}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  px: 1.5,
                                  py: 0.75,
                                  borderRadius: "8px",
                                  border: "1px solid",
                                  borderColor: selectedPermIds.includes(perm.id) ? "primary.main" : "rgba(74, 85, 101, 0.10)",
                                  bgcolor: selectedPermIds.includes(perm.id) ? "primary.light" : "#F9FAFB",
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: selectedPermIds.includes(perm.id) ? "primary.light" : "#F4F4F4" },
                                }}
                              >
                                <Checkbox checked={selectedPermIds.includes(perm.id)} size="small" sx={{ p: 0 }} />
                                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#000814", lineHeight: "21px" }}>{perm.name}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: "6px",
              border: "1px solid",
              borderColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
              padding: "6px 20px",
              textTransform: "none",
              "&:hover": { borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            variant="contained"
            sx={{
              borderRadius: "6px",
              border: "1px solid",
              borderColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
              padding: "6px 40px",
              textTransform: "none",
              "&:hover": { backgroundColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset" },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : editingRole ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openError} autoHideDuration={5000} onClose={handleSnackbarClose}>
        <Alert severity="error" variant="filled" sx={{ width: "100%" }}>{msg}</Alert>
      </Snackbar>
      <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openSuccess} autoHideDuration={5000} onClose={handleSnackbarClose}>
        <Alert severity="success" variant="filled" sx={{ width: "100%" }}>{msg}</Alert>
      </Snackbar>
    </>
  );
};
export default RoleDialog;
